<script>
import {
  formatMessageTime,
  formatMessageContent,
  formatPhone,
} from '@modules/atendimento/utils/message-formatter'
import {
  MEDIA_TYPES,
  ICONS,
  CHAT_COLORS,
  TASK_FROM_MESSAGE,
} from '@modules/atendimento/constants/Text'
import { FEATURES } from '@constants/Text'
import { apiAtendimento } from '@modules/atendimento/api/apiAtendimento'
import ChatMediaPreview from '@modules/atendimento/components/chat-media-preview/chat-media-preview.vue'
import ChatReplyPreview from '@modules/atendimento/components/chat-reply-preview/chat-reply-preview.vue'
import ChatMessageActions from '@modules/atendimento/components/chat-message-actions/chat-message-actions.vue'
import ChatBadge from '@modules/atendimento/components/chat-badge/chat-badge.vue'

// Indicador de canal por mensagem (opt-in via `showCanalBadge` — solicitacoes).
// WhatsApp × plataforma: cada canal tem rótulo + ícone próprios. Não usado no
// atendimento (default do prop = false), então o comportamento legado não muda.
const CANAL_WHATSAPP = 'whatsapp'
// Decisão de UX (03/07/2026): só sinalizar quando a mensagem foi por WhatsApp.
// Plataforma é o padrão e não precisa de badge. Se pedirem para exibir "Plataforma"
// de novo, descomente a const e a linha do config abaixo (o resto da lógica já suporta).
// const CANAL_PLATAFORMA = 'plataforma'
const CANAL_BADGE_CONFIG = {
  [CANAL_WHATSAPP]: { text: 'WhatsApp', icon: 'mdi-whatsapp', color: 'success' },
  // [CANAL_PLATAFORMA]: { text: 'Plataforma', icon: 'mdi-monitor', color: 'grey' },
}

export default {
  name: 'ChatMessage',
  components: {
    ChatMediaPreview,
    ChatReplyPreview,
    ChatMessageActions,
    ChatBadge,
  },
  props: {
    mensagem: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    // Id da tarefa vinculada a esta mensagem (resolvido pelo pai via store).
    // null = sem vínculo → badge não renderiza.
    linkedTaskId: {
      type: [String, Number],
      default: null,
    },
    // Seam de reuso atendimento↔solicitacoes — defaults = atendimento.
    api: {
      type: Object,
      default: () => apiAtendimento,
    },
    namespace: {
      type: String,
      default: 'atendimento',
    },
    // Feature flags opt-in pra esconder (boolean default false).
    // Solicitacoes passa hide-note + hide-task (sem nota/tarefa no menu) e
    // hide-phone-fallback (não mostra telefone como nome do autor).
    hideNote: {
      type: Boolean,
      default: false,
    },
    hideTask: {
      type: Boolean,
      default: false,
    },
    // Habilita a ação "Enviar para AAWZ Drive" no menu da mensagem — só quando
    // a conversa tem cliente vinculado (resolvido pelo chat-window).
    hasLinkedClient: {
      type: Boolean,
      default: false,
    },
    // Cliente vinculado — repassado ao menu de ações para ler a árvore do Drive
    // e servir de destino do upload.
    idClienteApp: {
      type: [String, Number],
      default: null,
    },
    hidePhoneFallback: {
      type: Boolean,
      default: false,
    },
    // Opt-in (solicitacoes): mostra o indicador de canal (WhatsApp × plataforma)
    // por mensagem. Default false mantém o atendimento sem badge de canal.
    showCanalBadge: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: ICONS,
      taskFromMessage: TASK_FROM_MESSAGE,
      hovered: false,
      pickerOpen: false,
      editingInline: false,
      editText: '',
      savingEdit: false,
    }
  },
  computed: {
    isIncoming() {
      return this.mensagem.direcao === 'entrada'
    },
    isOutgoing() {
      return this.mensagem.direcao === 'saida'
    },
    isNote() {
      return !!this.mensagem.is_note
    },
    isSystem() {
      return this.mensagem.tipo === 'sistema'
    },
    isTransfer() {
      return (
        this.isSystem &&
        this.mensagem.conteudo &&
        this.mensagem.conteudo.includes('transferi')
      )
    },
    isMediaType() {
      return MEDIA_TYPES.includes(this.mensagem.tipo)
    },
    hasMedia() {
      return !!this.resolvedMediaUrl
    },
    resolvedMediaUrl() {
      if (this.isMediaType && this.mensagem.id) {
        return this.api.buildMediaUrl(this.mensagem.id)
      }
      return null
    },
    hasReplyTo() {
      return !!(
        this.mensagem.reply_to_preview || this.mensagem.reply_to_message_id
      )
    },
    replyToSender() {
      if (!this.mensagem.reply_to_preview) return ''
      return this.mensagem.reply_to_preview.remetente_nome || ''
    },
    replyToContent() {
      if (!this.mensagem.reply_to_preview) return ''
      return this.mensagem.reply_to_preview.conteudo || ''
    },
    isEdited() {
      return !!this.mensagem.editada
    },
    senderName() {
      // Em grupo, o participante nem sempre expõe pushName (remetente_nome
      // vazio). Para mensagens recebidas, caímos no telefone formatado para
      // que o cabeçalho do autor nunca fique vazio (fidelidade ao WhatsApp).
      // DM recebida e mensagens próprias (saída) não carregam
      // remetente_telefone, então o comportamento atual é preservado.
      const nome = this.mensagem.remetente_nome || ''
      if (nome) return nome
      if (!this.hidePhoneFallback && this.isIncoming)
        return formatPhone(this.mensagem.remetente_telefone)
      return ''
    },
    noteProfessionalName() {
      return this.mensagem.nome_profissional_nota || ''
    },
    formattedTime() {
      return formatMessageTime(this.mensagem.data_mensagem)
    },
    statusClass() {
      return this.mensagem.status ? 'status-' + this.mensagem.status : ''
    },
    formattedContent() {
      return formatMessageContent(this.mensagem.conteudo)
    },
    isSticker() {
      return this.mensagem.tipo === 'sticker'
    },
    reacoes() {
      return this.mensagem.reacoes || []
    },
    hasReactions() {
      return this.reacoes.length > 0
    },
    reactionSummary() {
      // Group emojis: "👍x2 ❤️"
      const counts = {}
      for (const reacao of this.reacoes) {
        const emoji = reacao.emoji
        if (!counts[emoji]) counts[emoji] = { emoji, count: 0, names: [] }
        counts[emoji].count++
        counts[emoji].names.push(reacao.by || '')
      }
      return Object.values(counts)
    },
    bubbleClasses() {
      const classes = {}
      if (this.isSystem) {
        classes['message-bubble--system'] = true
      } else if (this.isNote) {
        classes['message-bubble--note'] = true
      } else if (this.isIncoming) {
        classes['message-bubble--incoming'] = true
      } else if (this.isOutgoing) {
        classes['message-bubble--outgoing'] = true
      }
      return classes
    },
    bubbleColor() {
      if (this.isNote) return CHAT_COLORS.BUBBLE_NOTE_BG
      if (this.isIncoming) return 'var(--white)'
      if (this.isOutgoing) return CHAT_COLORS.BUBBLE_OUTGOING_BG
      return 'var(--white)'
    },
    noteProfessionalColor() {
      return CHAT_COLORS.NOTE_PROFESSIONAL_COLOR
    },
    // Badge de tarefa só aparece com a flag TASK_V2 ligada (gating
    // defense-in-depth — sem ela, nem a busca de vínculos é disparada).
    canShowTaskBadge() {
      return this.$access[FEATURES.TASK_V2] === true
    },
    hasLinkedTask() {
      return this.canShowTaskBadge && !!this.linkedTaskId
    },
    alignClasses() {
      if (this.isSystem) return 'justify-center'
      if (this.isNote) return 'justify-center'
      if (this.isIncoming) return 'justify-start'
      return 'justify-end'
    },
    // Config do indicador de canal desta mensagem (rótulo + ícone + cor) ou
    // null quando o opt-in está desligado, é nota interna, ou o canal não é
    // conhecido. Mantém a lógica fora do template (regra AAWZ).
    canalBadge() {
      if (!this.showCanalBadge || this.isNote || this.isSystem) return null
      return CANAL_BADGE_CONFIG[this.mensagem.canal] || null
    },
    // Verdadeiro só na visão que trata o chat como entrega primária
    // (`showCanalBadge`, hoje Solicitações): uma mensagem de saída com
    // status='erro' e canal='whatsapp' está gravada no chat — apenas o relay
    // por WhatsApp falhou. Distingue do erro real de envio (bolha otimista sem
    // canal, quando a request nem chegou ao backend), que segue com retry.
    isWhatsappRelayFailed() {
      return (
        this.showCanalBadge &&
        this.isOutgoing &&
        !this.isNote &&
        this.mensagem.status === 'erro' &&
        this.mensagem.canal === CANAL_WHATSAPP
      )
    },
  },
  methods: {
    onReply(msg) {
      this.$emit('reply', msg)
    },
    onEdit(msg) {
      // Notas internas editam inline (dentro do próprio bubble) pra evitar
      // confusão com o campo principal de mensagem. Mensagens regulares de
      // WhatsApp continuam usando o fluxo do input principal.
      if (msg && msg.is_note) {
        this.editText = msg.conteudo || ''
        this.editingInline = true
        this.$nextTick(() => {
          const el = this.$refs.inlineEdit
          if (el) {
            el.focus()
            el.setSelectionRange(el.value.length, el.value.length)
          }
          this.scrollEditedNoteIntoView()
        })
        return
      }
      this.$emit('edit', msg)
    },
    scrollEditedNoteIntoView() {
      // Quando a textarea de edição inline aparece, o bubble cresce e pode
      // ficar cortado na parte de baixo (onde estão Cancelar/Salvar).
      // Scrollamos manualmente só o container `.chat-window__messages` (nunca
      // `scrollIntoView`, que sobe nos ancestrais e mexe no navbar).
      const scrollEl = this.$el.closest('.chat-window__messages')
      if (!scrollEl) return
      const elRect = this.$el.getBoundingClientRect()
      const scrollRect = scrollEl.getBoundingClientRect()
      if (elRect.bottom > scrollRect.bottom - 8) {
        const delta = elRect.bottom - scrollRect.bottom + 16
        scrollEl.scrollTo({
          top: scrollEl.scrollTop + delta,
          behavior: 'smooth',
        })
      }
    },
    cancelInlineEdit() {
      this.editingInline = false
      this.editText = ''
    },
    async saveInlineEdit() {
      const text = (this.editText || '').trim()
      if (!text) return
      if (this.savingEdit) return
      // Sem mudança real: só fecha
      if (text === (this.mensagem.conteudo || '')) {
        this.cancelInlineEdit()
        return
      }
      this.savingEdit = true
      try {
        const r = await this.$store.dispatch(`${this.namespace}/editarMensagem`, {
          id_mensagem: this.mensagem.id,
          conteudo: text,
        })
        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
        if (r && r.status === 200) {
          this.editingInline = false
          this.editText = ''
        }
      } finally {
        this.savingEdit = false
      }
    },
    onEditKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.saveInlineEdit()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.cancelInlineEdit()
      }
    },
    onNote(msg) {
      this.$emit('note', msg)
    },
    onCreateTask(msg) {
      this.$emit('create-task', msg)
    },
    onSendToDrive(payload) {
      this.$emit('send-to-drive', payload)
    },
    onSendToDriveRename(payload) {
      this.$emit('send-to-drive-rename', payload)
    },
    onOpenTask(taskId) {
      this.$emit('open-task', taskId)
    },
    onDelete(msg) {
      this.$emit('delete', msg)
    },
    onReact(data) {
      this.pickerOpen = false
      this.$emit('react', data)
    },
    onPickerToggle(open) {
      this.pickerOpen = open
    },
    onMouseLeave() {
      if (!this.pickerOpen) {
        this.hovered = false
      }
    },
    onReplyPreviewClick() {
      if (this.mensagem.reply_to_message_id) {
        this.$emit('scroll-to-message', this.mensagem.reply_to_message_id)
      }
    },
  },
}
</script>

<template>
  <div
    data-test="chat-message"
    class="chat-message mb-2"
    @mouseenter="hovered = true"
    @mouseleave="onMouseLeave"
  >
    <div class="d-flex" :class="alignClasses">
      <div
        v-if="isSystem"
        data-test="message-system"
        class="d-flex flex-column align-center"
      >
        <ChatBadge
          variant="system"
          :text="mensagem.conteudo"
          :icon="isTransfer ? icons.transfer : icons.info"
        />
        <span class="az-text-small az-muted message-time mt-1">{{
          formattedTime
        }}</span>
      </div>

      <div
        v-else
        :data-test="
          isNote
            ? 'message-note'
            : isIncoming
            ? 'message-incoming'
            : 'message-outgoing'
        "
        class="message-bubble pa-2 rounded"
        :class="bubbleClasses"
        style="position: relative"
      >
        <div v-if="isNote" data-test="note-header" class="mb-1">
          <ChatBadge variant="note" text="Nota interna" :icon="icons.lock" />
        </div>

        <div
          v-if="isNote && noteProfessionalName"
          data-test="note-professional"
          class="az-text-small font-medium mb-1"
          :style="{ color: noteProfessionalColor }"
        >
          {{ noteProfessionalName }}
        </div>

        <!-- Sender name (group messages + outgoing from shared connections) -->
        <div
          v-if="!isNote && senderName"
          data-test="sender-name"
          class="az-text-small font-medium mb-1 sender-name"
        >
          {{ senderName }}
        </div>

        <ChatReplyPreview
          v-if="hasReplyTo"
          :sender-name="replyToSender"
          :content="replyToContent"
          compact
          class="mb-1"
          @scroll-to="onReplyPreviewClick"
        />

        <ChatMediaPreview
          v-if="hasMedia"
          :api="api"
          :media-url="resolvedMediaUrl"
          :media-mime-type="mensagem.media_mime_type"
          :media-filename="mensagem.media_filename"
          :message-tipo="mensagem.tipo"
          class="mb-1"
        />

        <!-- Inline edit (notas internas) -->
        <div v-if="editingInline" class="inline-edit-wrapper">
          <textarea
            ref="inlineEdit"
            v-model="editText"
            class="inline-edit-textarea"
            rows="3"
            :disabled="savingEdit"
            @keydown="onEditKeydown"
          />
          <div class="d-flex justify-end mt-1" style="gap: 6px">
            <button
              type="button"
              class="inline-edit-btn"
              :disabled="savingEdit"
              @click="cancelInlineEdit"
              >Cancelar</button
            >
            <button
              type="button"
              class="inline-edit-btn inline-edit-btn--primary"
              :disabled="savingEdit || !editText.trim()"
              @click="saveInlineEdit"
            >
              <v-progress-circular
                v-if="savingEdit"
                indeterminate
                size="12"
                width="2"
                color="white"
                class="mr-1"
              />
              Salvar
            </button>
          </div>
        </div>

        <!-- eslint-disable vue/no-v-html — conteudo passa por formatMessageContent (escape XSS + markdown + linkify) -->
        <div
          v-else-if="mensagem.conteudo"
          class="az-text-primary az-size-14 message-text"
          v-html="formattedContent"
        />
        <!-- eslint-enable vue/no-v-html -->

        <div class="d-flex align-center justify-end mt-1">
          <span
            v-if="hasLinkedTask"
            data-test="message-task-badge"
            class="task-badge mr-1"
            :title="taskFromMessage.BADGE_TOOLTIP"
            @click.stop="onOpenTask(linkedTaskId)"
          >
            <ChatBadge
              variant="date"
              :text="taskFromMessage.BADGE_LABEL"
              :icon="taskFromMessage.BADGE_ICON"
            />
          </span>

          <span
            v-if="canalBadge"
            data-test="canal-badge"
            class="canal-indicator d-inline-flex align-center mr-1"
            :title="canalBadge.text"
          >
            <icon-svg
              :name="canalBadge.icon"
              size="12"
              :color="canalBadge.color"
              class="mr-1"
            />
            <span class="az-text-small az-muted">{{ canalBadge.text }}</span>
          </span>

          <span
            v-if="isEdited"
            data-test="edited-indicator"
            class="az-text-small az-muted mr-1"
            style="font-size: 10px; font-style: italic"
          >
            editada
          </span>

          <span class="az-text-small az-muted message-time">
            {{ formattedTime }}
          </span>

          <template v-if="isOutgoing && !isNote">
            <span
              v-if="mensagem.status === 'enviando'"
              data-test="status-enviando"
              class="status-icon ml-1"
            >
              <icon-svg :name="icons.statusSending" size="12" color="grey" />
            </span>
            <span
              v-else-if="mensagem.status === 'enviada'"
              data-test="status-enviada"
              class="status-icon ml-1"
            >
              <icon-svg :name="icons.statusSent" size="12" color="grey" />
            </span>
            <span
              v-else-if="mensagem.status === 'entregue'"
              data-test="status-entregue"
              class="status-icon ml-1"
            >
              <icon-svg :name="icons.statusDelivered" size="12" color="grey" />
            </span>
            <span
              v-else-if="mensagem.status === 'lida'"
              data-test="status-lida"
              class="status-icon ml-1"
            >
              <icon-svg :name="icons.statusDelivered" size="12" color="blue" />
            </span>
          </template>
        </div>

        <!-- Solicitacoes: o chat/plataforma é a entrega PRIMÁRIA; o WhatsApp é um
             relay opcional. Uma mensagem com status='erro' já está registrada no
             chat (o backend grava e retorna 200) — só a etapa do WhatsApp falhou.
             Não é "falha ao enviar" e não cabe retry (reenviar duplicaria a bolha
             no chat). Mostra um aviso discreto, não alarmante. -->
        <div
          v-if="isWhatsappRelayFailed"
          class="d-flex align-center mt-1"
          data-test="message-whatsapp-relay-failed"
        >
          <icon-svg
            :name="icons.whatsapp"
            size="12"
            color="grey"
            class="mr-1"
          />
          <span class="az-text-small az-muted" style="font-size: 11px">
            Enviada no chat — não entregue por WhatsApp
          </span>
        </div>

        <div
          v-else-if="mensagem.status === 'erro' && isOutgoing"
          class="d-flex align-center mt-1"
          data-test="message-error-banner"
        >
          <icon-svg
            :name="icons.statusError"
            size="12"
            color="red"
            class="mr-1"
          />
          <span class="error-text red--text">Falha ao enviar.</span>
          <span
            class="error-text ml-1 retry-link"
            data-test="retry-button"
            @click="$emit('retry', mensagem)"
          >
            Toque para tentar novamente
          </span>
        </div>

        <ChatMessageActions
          v-if="hovered"
          :mensagem="mensagem"
          :bubble-color="bubbleColor"
          :hide-note="hideNote"
          :hide-task="hideTask"
          :has-linked-client="hasLinkedClient"
          :id-cliente-app="idClienteApp"
          @reply="onReply"
          @edit="onEdit"
          @react="onReact"
          @picker-toggle="onPickerToggle"
          @note="onNote"
          @delete="onDelete"
          @create-task="onCreateTask"
          @send-to-drive="onSendToDrive"
          @send-to-drive-rename="onSendToDriveRename"
        />
      </div>
    </div>

    <div
      v-if="hasReactions"
      class="d-flex mb-1"
      :class="isOutgoing ? 'justify-end pr-2' : 'justify-start pl-2'"
    >
      <div class="reactions-row d-flex">
        <span
          v-for="(r, idx) in reactionSummary"
          :key="idx"
          class="reaction-chip"
          :title="r.names.join(', ')"
        >
          {{ r.emoji }}
          <span v-if="r.count > 1" class="reaction-count">{{ r.count }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
// ── Chat tokens (módulo atendimento) ─────────────────────────────
// Espelho dos valores exportados em `constants/Text.js → CHAT_COLORS`.
// JS e SASS NÃO compartilham imports: manter os dois em sincronia.
//
// Motivo: cores semanticamente específicas de mensageria (bolha
// WhatsApp, nota interna amber, link azul material) sem equivalente
// direto no design-system global (`_vars.sass`/`:root`). Extraídas
// aqui para evitar hex scattered e dar nome ao significado de cada cor.
//
// Cores com equivalente próximo no design-system NÃO viraram tokens:
// `var(--white)`, `var(--gray-3)` e `var(--gray-f4)` são usados direto.

$chat-outgoing-bg: #dcf8c6       // bolha de mensagem enviada — mint WhatsApp
$chat-note-bg: #FFF8E1           // bolha de nota interna — creme pastel
$chat-note-border: #FFE082       // borda da bolha de nota — amarelo claro
$chat-sender-name: #6B4FAF       // nome do remetente em mensagens de grupo — roxo
$chat-link: #1976D2              // cor de link em mensagens — material blue
$chat-link-hover: #1565C0        // hover do link — material blue escuro
$chat-reaction-count: #666666    // cor do contador de reações — cinza médio
$chat-inline-edit-border: #FFB300  // borda do textarea de edição inline — amber médio
$chat-inline-edit-primary: #F57F17 // bg/border do botão primário + focus do textarea — amber escuro
$chat-inline-edit-primary-hover: #E65100 // hover do botão primário — amber mais escuro

.message-bubble
  max-width: 65%
  word-wrap: break-word
  border-radius: 12px

// Reserva espaço no canto superior direito para o botão de ações flutuar
// sem sobrepor a primeira linha do texto. O rodapé (horário + visualizado)
// continua indo até a borda direita da bolha, igual ao WhatsApp.
.message-text::before
  content: ''
  float: right
  width: 28px
  height: 18px

.message-bubble--incoming
  background-color: var(--white)
  border: 1px solid var(--gray-3)

.message-bubble--outgoing
  background-color: $chat-outgoing-bg

.message-bubble--note
  background-color: $chat-note-bg
  border: 1px solid $chat-note-border
  max-width: 75%

.sender-name
  color: $chat-sender-name
  font-size: 12px

.message-time
  font-size: 10px

.task-badge
  cursor: pointer
  display: inline-flex

.status-icon
  display: inline-flex
  align-items: center

.error-text
  font-size: 10px

.retry-link
  font-size: 10px
  color: $chat-link
  cursor: pointer
  text-decoration: underline

.retry-link:hover
  color: $chat-link-hover

.message-text ::v-deep .message-link
  color: $chat-link
  text-decoration: underline
  word-break: break-all

.message-text ::v-deep .message-link:hover
  color: $chat-link-hover

.reactions-row
  gap: 4px

.reaction-chip
  background-color: var(--white)
  border: 1px solid var(--gray-3)
  border-radius: 12px
  padding: 1px 5px
  font-size: 16px
  line-height: 1.2
  cursor: default
  display: inline-flex
  align-items: center
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1)

.reaction-count
  font-size: 11px
  margin-left: 2px
  color: $chat-reaction-count

.inline-edit-wrapper
  margin: 4px 0

.inline-edit-textarea
  width: 100%
  min-width: 240px
  border: 1px solid $chat-inline-edit-border
  border-radius: 6px
  padding: 6px 8px
  font-size: 13px
  line-height: 1.4
  background-color: var(--white)
  resize: vertical
  outline: none
  font-family: inherit

.inline-edit-textarea:focus
  border-color: $chat-inline-edit-primary
  box-shadow: 0 0 0 1px $chat-inline-edit-primary

.inline-edit-btn
  background: none
  border: 1px solid var(--gray-3)
  border-radius: 4px
  padding: 3px 10px
  font-size: 11px
  cursor: pointer
  display: inline-flex
  align-items: center

.inline-edit-btn:hover:not(:disabled)
  background-color: var(--gray-f4)

.inline-edit-btn:disabled
  opacity: 0.5
  cursor: not-allowed

.inline-edit-btn--primary
  background-color: $chat-inline-edit-primary
  border-color: $chat-inline-edit-primary
  color: var(--white)

.inline-edit-btn--primary:hover:not(:disabled)
  background-color: $chat-inline-edit-primary-hover
  border-color: $chat-inline-edit-primary-hover
</style>

<!--
  Style NÃO-scoped. A classe `.message-highlight` é aplicada via JS em
  chat-window.vue (onScrollToMessage) no root do ChatMessage. Em <style scoped>,
  o seletor vira `.message-highlight[data-v-X] .message-bubble[data-v-X]` e
  a cascata com classes adicionadas por DOM API pode não bater direito.
  Global garante que funciona, e o nome da classe é específico o suficiente.
-->
<style lang="sass">
.message-highlight .message-bubble
  animation: chat-message-highlight-pulse 3s ease-in-out

@keyframes chat-message-highlight-pulse
  0%
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0)
  15%
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.18)
  30%
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0)
  45%
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.18)
  60%
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0)
  75%
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.18)
  100%
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0)
</style>
